import * as React from 'react';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useCurrentUser, usePermissions } from '@klareospace/pharos-lib-front-utils';
import {
  AuthenticatedLayout,
  Button,
  DefaultPendingPage,
  ImpersonationBanner,
  RouteErrorFallback,
} from '@klareospace/pharos-lib-web-utils';

import { useAuth } from '&/auth';
import { buildMasterContextSettingsNavItems, env, SIDEBAR_NAV_ITEMS } from '&/config';
import { ImportNotificationListener } from '&/features/imports';
import { OrderCreationDrawer } from '&/features/order-creation';
import { useOnboardingGate } from '&/features/onboarding-wizard/hooks/use-onboarding-gate';
import { useMasterAutoInit } from '&/hooks/use-master-auto-init';
import { useUserScopes } from '&/hooks/use-user-scopes';
import { SUPPORTED_LANGUAGES } from '&/i18n/config';
import { getAppVersionLabel } from '&/lib/app-version';
import { telemetryClient } from '&/lib/telemetry';
import { MasterContextProvider, useMasterContext } from '&/master';

function AuthenticatedErrorFallback({ error, reset }: ErrorComponentProps): React.JSX.Element {
  return (
    <RouteErrorFallback error={error} isDev={env.isDev} onError={telemetryClient.trackException} onReset={reset} />
  );
}

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: ({ context, location }): void => {
    // Don't redirect while auth state is still being determined (e.g., Firebase
    // hasn't fired onAuthStateChanged yet). The component will show a loading
    // page until isLoading becomes false.
    if (context.auth.isLoading) return;
    if (!context.auth.isAuthenticated) {
      throw redirect({
        search: { redirect: location.pathname },
        to: '/login',
      });
    }
  },
  component: FrontofficeAuthenticatedLayout,
  errorComponent: AuthenticatedErrorFallback,
});

function FrontofficeAuthenticatedLayout(): React.JSX.Element {
  return (
    <MasterContextProvider>
      <FrontofficeAuthenticatedLayoutInner />
    </MasterContextProvider>
  );
}

function FrontofficeAuthenticatedLayoutInner(): React.JSX.Element {
  const auth = useAuth();
  const { t } = useTranslation();
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions();
  const { user: backendUser } = useCurrentUser(auth.isAuthenticated);
  const scopeInfo = useUserScopes(auth.isAuthenticated ? (backendUser ?? null) : null);
  const masterContext = useMasterContext();
  const navigate = useNavigate();
  const onboardingGate = useOnboardingGate();
  const canCreateOrder = hasPermission('orders:write');
  // Mount-once: the drawer's queries (order types, sites, shippers...) never run for users who
  // never open it; once opened, it stays mounted so its own close animation/confirmation can play.
  const [hasOpenedCreateDrawer, setHasOpenedCreateDrawer] = React.useState(false);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = React.useState(false);

  useMasterAutoInit({ scopeInfo, user: backendUser });

  // Redirect eligible tenant admins to the onboarding wizard shell before any
  // authenticated page renders — avoids a home-page flash.
  React.useEffect(() => {
    if (onboardingGate.shouldShowWizard) {
      void navigate({ to: '/onboarding' });
    }
  }, [onboardingGate.shouldShowWizard, navigate]);

  if (onboardingGate.isLoading || onboardingGate.shouldShowWizard) {
    return <DefaultPendingPage />;
  }

  const footerNavItems = buildMasterContextSettingsNavItems({
    ...scopeInfo,
    siteId: masterContext.siteId,
    spaceId: masterContext.spaceId,
  });

  const handleDisconnect = async (): Promise<void> => {
    await auth.signOut();
    // After sign-out, the auth state will become unauthenticated
    // and the beforeLoad guard will redirect to /login
  };

  function handleCreateOrder(): void {
    setHasOpenedCreateDrawer(true);
    setIsCreateDrawerOpen(true);
  }

  function handleCloseCreateDrawer(): void {
    setIsCreateDrawerOpen(false);
  }

  const topAction = canCreateOrder ? (
    <Button aria-label={t('navigation.newAriaLabel')} className="w-full" onClick={handleCreateOrder} type="button">
      <Plus className="h-4 w-4" />
      {t('navigation.new')}
    </Button>
  ) : undefined;

  const topBanner = auth.isImpersonating ? (
    <ImpersonationBanner
      disconnectLabel={t('impersonation.disconnect')}
      message={
        <Trans
          components={{ strong: <strong /> }}
          i18nKey="impersonation.viewingAs"
          values={{ email: auth.user?.email }}
        />
      }
      onDisconnect={(): void => void handleDisconnect()}
    />
  ) : undefined;

  return (
    <>
      <ImportNotificationListener />
      <AuthenticatedLayout
        auth={auth}
        backendUser={backendUser}
        footerNavItems={footerNavItems}
        hasPermission={hasPermission}
        isPermissionsLoading={isPermissionsLoading}
        languages={[...SUPPORTED_LANGUAGES]}
        logo={
          <Link className="flex flex-col items-start" to="/">
            <img
              alt={t('app.logoAlt')}
              className="h-7 max-w-32 object-contain group-data-[collapsible=icon]:hidden"
              src={`${import.meta.env.BASE_URL}klareo-logo-white.png`}
            />
            <div className="hidden h-8 w-8 items-center justify-center rounded-full bg-sidebar-primary group-data-[collapsible=icon]:flex">
              <span className="text-sm font-bold text-white">K</span>
            </div>
            <p className="mt-0.5 text-xs leading-tight text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden">
              {t('app.subtitle')}
            </p>
          </Link>
        }
        navItems={SIDEBAR_NAV_ITEMS}
        requiredPermission="frontoffice:access"
        topAction={topAction}
        topBanner={topBanner}
        versionLabel={getAppVersionLabel()}
      />
      {hasOpenedCreateDrawer && <OrderCreationDrawer onClose={handleCloseCreateDrawer} open={isCreateDrawerOpen} />}
    </>
  );
}
